export default class Pirsch {
    constructor(settings) {
        this.bindUnload();
        this.settings = settings ?? window.interface_settings?.settings?.analytics;
        this.url = this.settings.stats_url;
        this.scope = this.settings.pirsch_scope ?? "maglr.dev";
        this.sendhit = true;
        this.isBeacon = false;
        this.inActiveTimer = false;
        this.disabled = false; // used for cookie consent
        // disable stats by: url parameter (frontend/embed) || settings (inject) || default false
        this.disableStats =
            new URLSearchParams(window.location.search).get("disable-stats") || settings.disableStats || false;
    }

    interceptor(spreadId = undefined) {
        if (this.settings) {
            if (spreadId) {
                this.interceptorSpreadId = spreadId;
            }
            window.addEventListener("message", (event) => this.messageReceived(event), false);
        }

        return this;
    }

    getFrontendInstance() {
        return typeof window.frontend === "object" ? window.frontend : false;
    }

    callFrontendFunction(functionName) {
        const frontend = this.getFrontendInstance();
        return frontend && frontend[functionName].bind(frontend);
    }
    messageReceived({ data = {} }) {
        if (data.message === "USER_EVENT") {
            if (this.interceptorSpreadId && this.interceptorSpreadId !== data.origin_spread_id) {
                return;
            }
            this.sendEventUpdate(data, data.origin_issue_id, data.origin_spread_id);
        }
    }
    unload() {
        this.isBeacon = true;
    }

    bindUnload() {
        // Save the bound function reference
        this.boundUnload = this.unload.bind(this);

        window.addEventListener("beforeunload", this.boundUnload);
        this.resetUnload();
    }

    resetUnload() {
        clearTimeout(this.inActiveTimer);
        this.inActiveTimer = setTimeout(() => {
            // Remove the saved bound function reference
            window.removeEventListener("beforeunload", this.boundUnload);
        }, 900000); // 1000 * 60 * 15 minutes = 900 000
    }

    trigger(path, type, payload = null) {
        if (this.isBeacon) {
            navigator.sendBeacon && navigator.sendBeacon(`${this.url}/${path}`, payload);
        } else {
            const req = new XMLHttpRequest();
            req.open(type, `${this.url}/${path}`);
            if (payload) {
                req.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
            }
            req.send(payload);
        }
    }
    sendEventUpdate(originalData, originIssueId, originSpreadId) {
        if (this.disableStats || this.disabled || !this.url) return;

        try {
            let data = JSON.parse(JSON.stringify(originalData));
            const frontendInstance = this.getFrontendInstance();
            frontendInstance?.bounceRateTimer && clearTimeout(frontendInstance.bounceRateTimer);

            this.resetUnload();
            if (data.dataGA3) {
                data = Object.assign(data.dataGA3, data);
            }
            if (data.dataGA4?.data) {
                data = Object.assign(data.dataGA4.data, data);
            }

            if (originIssueId && originSpreadId) {
                data.issue_id = originIssueId;
                data.spread_id = originSpreadId;
                data.page = frontendInstance
                    ? frontendInstance.dataHelper.getSpreadById(originIssueId, originSpreadId)?.url
                    : location.pathname;
            }

            if (frontendInstance?.dataHelper) {
                const issue = frontendInstance.dataHelper.getIssueByIssueId(originIssueId);
                const spread = frontendInstance.dataHelper.getSpreadById(originIssueId, originSpreadId);

                if (Object.hasOwn(data, "issue_title")) data.issue_title = issue.issue_title;
                if (Object.hasOwn(data, "spread_title")) data.spread_title = spread.spread_title;
                if (Object.hasOwn(data, "page_title")) data.page_title = spread.spread_title;
            }

            this.send(data);
        } catch (e) {
            console.warn("Pirsch sendEventUpdate error: ", e);
        }
    }

    send(data) {
        const { url, meta } = this.createConfigAndMeta(data);
        data.hitType === "page_view" ? this.pageHit(url, data) : this.pageEvent(url, data, meta);
    }
    pageEvent(url, data, meta) {
        const payload = {
            url,
            token: this.settings.pirsch_token,
            screen_width: screen.width,
            screen_height: screen.height,
            page: data.page,
            event_name: (data.eventCategory ?? data.type ?? "").toLowerCase().replace(/-/g, "_"),
            event_meta: this.castObjectValuesToString(this.keysToLowerCase(meta)),
        };
        this.addReferrerIfValid(payload);

        this.trigger("event", "POST", JSON.stringify(this.keysToLowerCase(payload)));
    }

    addReferrerIfValid(payload) {
        if (document.referrer && new URL(document.referrer).hostname !== location.hostname) {
            payload.referrer = document.referrer;
        }
    }

    keysToLowerCase(obj) {
        return Object.entries(obj).reduce((result, [key, value]) => {
            result[key.toLowerCase()] = value;
            return result;
        }, {});
    }

    castObjectValuesToString(obj) {
        return Object.entries(obj).reduce((result, [key, value]) => {
            result[key] = String(value);
            return result;
        }, {});
    }

    pageHit(url, data) {
        const frontendInstance = this.getFrontendInstance();
        if (!frontendInstance || this.callFrontendFunction("getCurrentIssueId")) {
            this.sendhit = true;
        }

        if (this.sendhit) {
            const payload = {
                url,
                token: this.settings.pirsch_token,
                screen_width: screen.width,
                screen_height: screen.height,
                page: data.page || location.pathname,
                title: document.title,
            };
            this.addReferrerIfValid(payload);

            this.trigger(`hit?${new URLSearchParams(payload).toString()}`, "GET");

            if (frontendInstance?._homepage) {
                // TODO: dit uitzoeken waarvoor dit is
                this.sendhit = false;
            }
        }
    }

    createConfigAndMeta(data) {
        const frontendInstance = this.getFrontendInstance();
        const meta = {};
        const clientId = this.settings.client_id ?? data.client_id ?? window.client_id;
        const embedHash = this.settings.embedHash ?? window.embedHash;
        const domainId = embedHash ? "embed" : (data.domain_id ?? window.domain_id);

        meta.action = (data.eventAction || data.action || "").toLowerCase().replace(/-/g, "_");

        if (data.action === "Internal" && window.maglrShorttags) {
            const spread_id = parseInt(data.label.split("::")[0].trim().split("|")[1], 10);
            data.label =
                Object.values(window.maglrShorttags).find(
                    (shortTagData) => shortTagData.js_spread_spread_id === spread_id,
                )?.url ?? data.label;
        }

        meta.label = data.eventLabel || data.label;
        if (meta.label && typeof meta.label === "string") {
            meta.label = meta.label.split("::")[0].trim();
        }

        if (meta.action === "gate_form_submitted") {
            meta.form_hash = data.eventValue;
        }

        if (frontendInstance?._homepage) {
            const category = new URLSearchParams(document.location.search).get("category");
            if (category) {
                meta.category = category;
            }
        } else {
            meta.issue_id = data.issue_id ?? window.issue_id;
            meta.spread_id = data.spread_id ?? window.spread_id;
        }

        if (data.element_id) meta.element_id = data.element_id;
        if (data.element_name) meta.element_name = data.element_name;

        let viewMode = "publication";
        const proSinglePage = document.querySelector(".singlePage") !== null;
        if (proSinglePage || window.embedmode === "singlepage") {
            // checks for both pro || template singlepage
            viewMode = "singlepage";
        }
        if (window.mail_hash) {
            viewMode = `personal_${window.mail_hash}`;
        } else if (embedHash) {
            viewMode = `embed_${embedHash}`;
        } else if (window.exportType === "ipad") {
            viewMode = "presenter";
        }

        const path = [clientId, domainId, viewMode, meta.issue_id || "overview"];
        if (meta.spread_id) {
            path.push(meta.spread_id);
        }
        if (
            data.layout ||
            (meta.spread_id && (frontendInstance?.getSpreadType?.(meta.spread_id) === 2 || proSinglePage))
        ) {
            path.push(data.layout ?? (Math.min(window.innerWidth, window.screen.width) < 501 ? 2 : 1));
        }

        return { meta, url: `https://${clientId}.${this.scope}/${path.join("/")}${location.search}` };
    }
}
